// Banding layer: numeric features → categorical findings.
//
// Thresholds are SEED defaults (literature-informed + conservative). They are
// versioned so longitudinal comparisons stay apples-to-apples and so a future
// calibration study can replace them without breaking stored data.
//
// The underlying magnitudes are kept on each Finding for scoring/debugging but
// are never shown to the user as clinical values — the UI renders only the band.

import type { FrontalFeatures } from "./frontal";
import type { LateralFeatures } from "./lateral";
import type { TemporalSummary } from "./temporal";
import type { Side } from "../pose/landmarks";
import { clamp } from "./geometry";

export const THRESHOLD_VERSION = "phase2-seed-2";

// Frontal x-based features (stepWidth, trunkLean, kneeCollapse) are horizontal
// offsets normalized by torso length. Since phase2-2-iso they are computed in
// isotropic (square-pixel) space, where they become true body-relative ratios,
// aspect-independent. The original seeds were tuned on the old *anisotropic*
// scale (x = fraction of frame width), so they sat at value × aspect of the
// isotropic ratio. Frontal capture is portrait (walk toward/away from camera),
// so the implicit reference aspect was ~9/16. We rescale x-based seeds by this
// factor to keep banding sensitivity at the typical capture while being
// geometrically correct for any aspect. y-based (pelvic obliquity) and ratio
// (asymmetry index) features are aspect-invariant and are left unchanged.
// These remain seed values pending a calibration study.
const FRONTAL_ISO = 9 / 16; // ≈ 0.5625

export type DeviationBand =
  | "NORMAL"
  | "MILD DEVIATION"
  | "MODERATE DEVIATION"
  | "SEVERE DEVIATION";
export type TendencyBand = "LOW" | "MODERATE" | "HIGH";
export type Confidence = "high" | "medium" | "low";

export interface Finding {
  key: string;
  label: string;
  scale: "deviation" | "tendency";
  band: DeviationBand | TendencyBand;
  magnitude: number;
  confidence: Confidence;
  side?: Side;
  detail?: string;
}

const DEVIATION_BANDS: DeviationBand[] = [
  "NORMAL",
  "MILD DEVIATION",
  "MODERATE DEVIATION",
  "SEVERE DEVIATION",
];
const TENDENCY_BANDS: TendencyBand[] = ["LOW", "MODERATE", "HIGH"];

/** Map a magnitude to a band given ascending cut points. */
function bandFromCuts<T>(value: number, cuts: number[], bands: T[]): T {
  let i = 0;
  while (i < cuts.length && value >= cuts[i]) i++;
  return bands[i];
}

function deviation(value: number, cuts: [number, number, number]): DeviationBand {
  return bandFromCuts(value, cuts, DEVIATION_BANDS);
}
function tendency(value: number, cuts: [number, number]): TendencyBand {
  return bandFromCuts(value, cuts, TENDENCY_BANDS);
}

/** Confidence from capture quality: visibility + number of cycles. */
export function deriveConfidence(meanVisibility: number, cycleCount: number): Confidence {
  if (meanVisibility >= 0.7 && cycleCount >= 3) return "high";
  if (meanVisibility >= 0.55 && cycleCount >= 2) return "medium";
  return "low";
}

const CONFIDENCE_RANK: Record<Confidence, number> = { low: 0, medium: 1, high: 2 };

/** Return the weaker (more conservative) of two confidences. */
export function minConfidence(a: Confidence, b: Confidence): Confidence {
  return CONFIDENCE_RANK[a] <= CONFIDENCE_RANK[b] ? a : b;
}

export interface BuildFindingsInput {
  view: "frontal" | "lateral";
  temporal: TemporalSummary;
  frontal?: FrontalFeatures;
  lateral?: LateralFeatures;
  meanVisibility: number;
  cycleCount: number;
  /**
   * Optional per-side capture quality. When provided, side-specific findings are
   * capped at the confidence that side's own visibility/cycles support — so the
   * occluded far leg of a lateral clip is reported more cautiously than the
   * camera-side leg. Omitted ⇒ side findings use the global confidence (legacy).
   */
  sideVisibility?: Record<Side, number>;
  sideCycleCount?: Record<Side, number>;
}

const KNEE_FLEX_REF = 50; // expected dynamic knee flexion excursion (internal deg)

export function buildFindings(input: BuildFindingsInput): Finding[] {
  const conf = deriveConfidence(input.meanVisibility, input.cycleCount);
  // Per-side confidence: never above the global value, optionally lower if that
  // side's own capture quality is worse (e.g. occluded far leg). No side info ⇒
  // falls back to the global confidence exactly.
  const sideConf = (s: Side): Confidence => {
    if (!input.sideVisibility) return conf;
    const cc = input.sideCycleCount?.[s] ?? input.cycleCount;
    return minConfidence(conf, deriveConfidence(input.sideVisibility[s], cc));
  };
  const out: Finding[] = [];

  // --- Temporal (both views) ---
  if (input.temporal.stanceSymmetryIndex !== null) {
    out.push({
      key: "stance_symmetry",
      label: "Stance-time symmetry",
      scale: "deviation",
      band: deviation(input.temporal.stanceSymmetryIndex, [5, 10, 18]),
      magnitude: input.temporal.stanceSymmetryIndex,
      confidence: conf,
    });
  }
  out.push({
    key: "cycle_consistency",
    label: "Gait-cycle consistency",
    scale: "deviation",
    band: deviation(input.temporal.cycleConsistencyCV * 100, [5, 10, 18]),
    magnitude: input.temporal.cycleConsistencyCV * 100,
    confidence: conf,
  });

  // --- Frontal ---
  if (input.view === "frontal" && input.frontal) {
    const fr = input.frontal;
    out.push({
      key: "pelvic_drop",
      label: "Pelvic drop",
      scale: "deviation",
      band: deviation(fr.pelvicDrop, [0.03, 0.05, 0.08]),
      magnitude: fr.pelvicDrop,
      confidence: conf,
    });
    out.push({
      key: "trendelenburg",
      label: "Trendelenburg pattern",
      scale: "tendency",
      band: tendency(fr.trendelenburg, [0.34, 0.67]),
      magnitude: fr.trendelenburg,
      confidence: conf,
    });
    (["L", "R"] as Side[]).forEach((s) =>
      out.push({
        key: `knee_collapse_${s}`,
        label: `Dynamic knee collapse (${s})`,
        scale: "tendency",
        // x-based → iso-rescaled: [0.04, 0.07] × 9/16
        band: tendency(fr.kneeCollapse[s], [0.04 * FRONTAL_ISO, 0.07 * FRONTAL_ISO]),
        magnitude: fr.kneeCollapse[s],
        confidence: sideConf(s),
        side: s,
      }),
    );
    out.push({
      key: "trunk_lean",
      label: "Lateral trunk lean",
      scale: "deviation",
      // x-based → iso-rescaled: [0.04, 0.07, 0.1] × 9/16
      band: deviation(fr.trunkLean, [0.04 * FRONTAL_ISO, 0.07 * FRONTAL_ISO, 0.1 * FRONTAL_ISO]),
      magnitude: fr.trunkLean,
      confidence: conf,
    });
    out.push({
      key: "step_width",
      label: "Step width",
      scale: "deviation",
      // x-based → iso-rescaled: [0.5, 0.65, 0.8] × 9/16
      band: deviation(fr.stepWidth, [0.5 * FRONTAL_ISO, 0.65 * FRONTAL_ISO, 0.8 * FRONTAL_ISO]),
      magnitude: fr.stepWidth,
      confidence: conf,
    });
    out.push({
      key: "frontal_asymmetry",
      label: "Side-to-side asymmetry",
      scale: "deviation",
      band: deviation(fr.asymmetryIndex, [10, 20, 30]),
      magnitude: fr.asymmetryIndex,
      confidence: conf,
    });
  }

  // --- Lateral ---
  if (input.view === "lateral" && input.lateral) {
    const lt = input.lateral;
    (["L", "R"] as Side[]).forEach((s) => {
      const deficit = clamp(KNEE_FLEX_REF - lt.kneeFlexionExcursion[s], 0, 999);
      out.push({
        key: `knee_flexion_${s}`,
        label: `Knee flexion pattern (${s})`,
        scale: "deviation",
        band: deviation(deficit, [8, 18, 28]),
        magnitude: deficit,
        confidence: sideConf(s),
        side: s,
      });
      out.push({
        key: `terminal_extension_${s}`,
        label: `Terminal knee extension (${s})`,
        scale: "deviation",
        band: deviation(lt.terminalExtensionDeficit[s], [8, 15, 25]),
        magnitude: lt.terminalExtensionDeficit[s],
        confidence: sideConf(s),
        side: s,
      });
      out.push({
        key: `hip_extension_${s}`,
        label: `Hip extension (${s})`,
        scale: "deviation",
        band: deviation(lt.hipExtensionLimitation[s], [8, 15, 25]),
        magnitude: lt.hipExtensionLimitation[s],
        confidence: sideConf(s),
        side: s,
      });
    });
    out.push({
      key: "trunk_posture",
      label: "Trunk posture (forward lean)",
      scale: "deviation",
      band: deviation(lt.trunkForwardLean, [8, 14, 20]),
      magnitude: lt.trunkForwardLean,
      confidence: conf,
    });
    out.push({
      key: "stride_regularity",
      label: "Stride regularity",
      scale: "deviation",
      band: deviation(lt.strideRegularityCV * 100, [8, 15, 25]),
      magnitude: lt.strideRegularityCV * 100,
      confidence: conf,
    });
    out.push({
      key: "smoothness",
      label: "Gait smoothness",
      scale: "deviation",
      band: deviation(lt.smoothnessJerk, [0.15, 0.3, 0.5]),
      magnitude: lt.smoothnessJerk,
      confidence: conf,
    });
  }

  return out;
}
